/**
 * PATH: apps/quote/src/components/builder/ScopeSheetGrid.tsx
 * PURPOSE: Unified virtualized spreadsheet grid for all quote sections.
 * WHY: Single virtualized grid handles 1000s of rows with keyboard nav + inline editing.
 * FLOW: Quote sections -> flatten -> virtualize -> render visible -> handle edits
 */

import { useMemo, useRef, useState, useCallback, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Focus, X } from 'lucide-react'
import type { QuoteLineItem } from '@/lib/quoteApi'
import PhantomRow from './PhantomRow'
import { useWorkspace } from './BuilderWorkspace'
import { useSpreadsheetGrid, type ColumnDef } from '@/hooks/useSpreadsheetGrid'
import { evaluateCell } from '@/lib/quote-builder-math/formula'
import PasteMappingModal from './PasteMappingModal'

// Sub-modules
import type { ScopeSheetGridProps } from './scope-sheet-grid/ScopeSheetGridTypes'
import { ROW_HEIGHTS } from './scope-sheet-grid/ScopeSheetGridTypes'
import {
  BASE_COLUMN_BANDS,
  getGridColsStyle,
  applyGridViewPreset,
  isNumericColumn,
  buildCustomColumnBand,
  applyOptionalColumnVisibility,
} from './scope-sheet-grid/scope-sheet-columns'
import { flattenToRows } from './scope-sheet-grid/scope-sheet-utils'
import SectionHeaderRow from './scope-sheet-grid/SectionHeaderRow'
import LineItemRow from './scope-sheet-grid/LineItemRow'
import ColumnHeaders from './scope-sheet-grid/ColumnHeaders'
import RowContextMenu, { type RowContextAction } from './scope-sheet-grid/RowContextMenu'
import useScopeSheetPaste from './scope-sheet-grid/use-scope-sheet-paste'

export type { ScopeSheetGridProps } from './scope-sheet-grid/ScopeSheetGridTypes'

export default function ScopeSheetGrid({
  sections, isEditable, onAddItem, onUpdateItem, onDeleteItem,
  onAddSection, onRenameSection, onDeleteSection, onSelectItem, selectedItemId,
  isAddingSectionPending = false, focusedSectionId: externalFocusedId, onFocusSection,
  onPasteWithCreate, getOptimisticValue, getCellErrorState, isSaving = false,
  requestedPhantomFocusSectionId, onRequestedPhantomFocusHandled,
  requestedScrollToSectionId, onRequestedScrollToSectionHandled,
  externalPaste, onExternalPasteHandled,
  configDefaults,
  customColumns,
  visibleColumnIds,
  onUpdateConfig,
  onDuplicateItem,
  onInsertRowAbove,
  onInsertRowBelow,
}: ScopeSheetGridProps) {
  const { gridView, activeSheet, activateAssumptions } = useWorkspace()
  const parentRef = useRef<HTMLDivElement>(null)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())

  // Right-click row context menu — single shared instance for the whole grid
  // (one menu open at a time, anchored to the click coordinates).
  const [rowMenu, setRowMenu] = useState<{
    x: number
    y: number
    sectionId: string
    itemId: string
  } | null>(null)

  // Focus mode (externally controlled or internal)
  const [internalFocusedId, setInternalFocusedId] = useState<string | null>(null)
  const focusedSectionId = externalFocusedId !== undefined ? externalFocusedId : internalFocusedId
  const handleFocusSection = useCallback(
    (id: string | null) => (onFocusSection ? onFocusSection(id) : setInternalFocusedId(id)),
    [onFocusSection],
  )
  const toggleFocus = useCallback(
    (id: string) => handleFocusSection(focusedSectionId === id ? null : id),
    [focusedSectionId, handleFocusSection],
  )

  // Flatten + virtualise
  const rows = useMemo(() => flattenToRows(sections, collapsedSections, focusedSectionId), [sections, collapsedSections, focusedSectionId])
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => ROW_HEIGHTS[rows[i].type] || 36,
    overscan: 10,
  })
  const toggleCollapse = useCallback((id: string) => {
    setCollapsedSections((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }, [])
  const virtualItems = virtualizer.getVirtualItems()
  const focusedSection = focusedSectionId ? sections.find((s) => s.id === focusedSectionId) : null
  const totalLineItems = useMemo(() => sections.reduce((sum, s) => sum + (s.items?.length || 0), 0), [sections])

  // Column configuration — base bands first, then a dynamically-built "Custom"
  // band assembled from the quote's user-defined columns (if any).
  const columnBands = useMemo(() => {
    // Order of operations matters:
    //  1. Layer in optional-column visibility (per-quote opt-in) FIRST so
    //     the preset can still hide them via gridView=compact.
    //  2. Apply the global gridView preset (compact vs full).
    //  3. Append the custom band last so it sits at the right edge.
    const withOptionals = applyOptionalColumnVisibility(
      BASE_COLUMN_BANDS,
      visibleColumnIds ?? [],
    )
    const presetBands = applyGridViewPreset(gridView, withOptionals)
    const customBand = buildCustomColumnBand(customColumns)
    return customBand ? [...presetBands, customBand] : presetBands
  }, [gridView, customColumns, visibleColumnIds])
  const allColumns = useMemo(() => columnBands.flatMap((b) => b.columns), [columnBands])
  // Advanced (opt-in) columns — extracted from columnBands AFTER visibility +
  // preset have been applied, so each Column carries its current `hidden`
  // state. Positional ordering matches band order. Empty array if the
  // band wasn't included for any reason — defensive.
  const advancedColumns = useMemo(
    () => columnBands.find((b) => b.id === 'advanced')?.columns ?? [],
    [columnBands],
  )
  const gridStyle = useMemo(() => getGridColsStyle(allColumns), [allColumns])
  const columnDefs: ColumnDef[] = useMemo(() => allColumns.map((col) => ({
    key: col.id, label: col.label,
    width: col.hidden ? 0 : col.width === 'flex' ? 200 : col.width,
    editable: !!col.editable && !col.hidden, hidden: !!col.hidden,
    type: col.custom
      ? (col.type === 'number' || col.type === 'currency' ? 'number' : 'text') as const
      : isNumericColumn(col.id) ? 'number' as const : 'text' as const,
    align: col.align || 'left',
  })), [allColumns])

  // Flat line-item index (visible, expanded items only)
  const flatLineItems = useMemo(() => {
    const items: { sectionId: string; itemId: string; item: QuoteLineItem; rowIndex: number }[] = []
    let idx = 0
    for (const section of sections) {
      if (focusedSectionId && section.id !== focusedSectionId) continue
      if (collapsedSections.has(section.id) && !focusedSectionId) continue
      for (const item of section.items || []) {
        items.push({ sectionId: section.id, itemId: item.id, item, rowIndex: idx++ })
      }
    }
    return items
  }, [sections, focusedSectionId, collapsedSections])

  // Cell change / read callbacks
  // Mirrors LineItemRow.handleCommit's formula-aware logic so that commits
  // routed through useSpreadsheetGrid (Enter/Tab from active cell) persist
  // "=expr" identically to commits routed through EditableCell.onBlur. If
  // the value is a formula, we cache the evaluated number AND store the raw
  // expression in `formulas[field]`. Otherwise we clear the formula entry.
  const handleCellChange = useCallback((rowIndex: number, colIndex: number, value: string) => {
    const flat = flatLineItems[rowIndex]
    if (!flat) return
    const col = allColumns[colIndex]
    if (!col || col.hidden || !col.editable) return
    const patch: Record<string, unknown> = {}
    const existingFormulas = (flat.item.formulas ?? {}) as Record<string, string>
    const existingCustom = (flat.item.custom_values ?? {}) as Record<string, unknown>

    // Custom-column path: writes go through `custom_values[key]`, never the
    // typed columns. We don't support `=formulas` in custom columns yet —
    // they're plain scalar overrides.
    if (col.custom) {
      let coerced: unknown = value
      if (col.type === 'number' || col.type === 'currency') {
        coerced = value === '' || value === null || value === undefined
          ? null
          : Number.parseFloat(value)
        if (Number.isNaN(coerced)) coerced = null
      }
      patch.custom_values = { ...existingCustom, [col.id]: coerced }
      onUpdateItem(flat.sectionId, flat.itemId, patch)
      return
    }

    if (typeof value === 'string' && value.trim().startsWith('=')) {
      // Build a minimal eval context from the line item; the row component
      // builds a richer one with aliases, but for the spreadsheet-level
      // commit path we use the canonical fields only — good enough to cache
      // a numeric, and the cell will re-evaluate with full context on next
      // render anyway.
      const N = (v: unknown): number => {
        const n = Number.parseFloat(String(v ?? 0))
        return Number.isFinite(n) ? n : 0
      }
      const ctx = {
        quantity: N(flat.item.quantity), qty: N(flat.item.quantity),
        productivity_pmpd: N(flat.item.productivity_pmpd), pmpd: N(flat.item.productivity_pmpd),
        labour_chargeout: N(flat.item.labour_chargeout), labour: N(flat.item.labour_chargeout),
        material_chargeout: N(flat.item.material_chargeout), material: N(flat.item.material_chargeout),
        plant_chargeout: N(flat.item.plant_chargeout), plant: N(flat.item.plant_chargeout),
        subcon_chargeout: N(flat.item.subcon_chargeout), subcon: N(flat.item.subcon_chargeout),
        adjusted_rate: N(flat.item.adjusted_rate), rate: N(flat.item.adjusted_rate),
      }
      const result = evaluateCell(value, ctx)
      patch.formulas = { ...existingFormulas, [col.id]: value }
      patch[col.id] = isNumericColumn(col.id)
        ? (result.ok ? result.value : null)
        : (result.ok ? String(result.value) : value)
      onUpdateItem(flat.sectionId, flat.itemId, patch)
      return
    }

    if (col.id in existingFormulas) {
      const next = { ...existingFormulas }
      delete next[col.id]
      patch.formulas = next
    }
    patch[col.id] = isNumericColumn(col.id) ? (value ? parseFloat(value) : null) : value
    onUpdateItem(flat.sectionId, flat.itemId, patch)
  }, [flatLineItems, onUpdateItem, allColumns])

  const getCellValue = useCallback((rowIndex: number, colIndex: number): string => {
    const flat = flatLineItems[rowIndex]
    if (!flat) return ''
    const col = allColumns[colIndex]
    if (!col || col.hidden) return ''
    // Custom-column read: cascade line.custom_values[key] -> column.defaultValue.
    if (col.custom) {
      const customMap = (flat.item.custom_values ?? {}) as Record<string, unknown>
      const lineVal = customMap[col.id]
      if (lineVal !== undefined && lineVal !== null && lineVal !== '') {
        return String(lineVal)
      }
      if (col.defaultValue !== undefined && col.defaultValue !== null) {
        return String(col.defaultValue)
      }
      return ''
    }
    // If the cell has a persisted "=..." formula, seed the editor with the
    // formula text so the user can amend the expression instead of replacing
    // it with the cached numeric value (Excel parity).
    const formulasMap = (flat.item.formulas ?? {}) as Record<string, string>
    const cellFormula = formulasMap[col.id]
    if (cellFormula && typeof cellFormula === 'string' && cellFormula.trim().startsWith('=')) {
      return cellFormula
    }
    const opt = getOptimisticValue?.(flat.itemId, col.id)
    if (opt !== undefined) return String(opt ?? '')
    return String(flat.item[col.id as keyof QuoteLineItem] ?? '')
  }, [flatLineItems, getOptimisticValue, allColumns])

  // Paste logic (extracted hook)
  const paste = useScopeSheetPaste({ sections, flatLineItems, allColumns, onUpdateItem, onPasteWithCreate, handleCellChange })

  // Spreadsheet keyboard navigation
  const spreadsheet = useSpreadsheetGrid({
    columns: columnDefs, rowCount: flatLineItems.length,
    onCellChange: handleCellChange, getCellValue, onPaste: paste.handlePasteData,
  })

  // External paste trigger (bottom-bar Paste button)
  useEffect(() => {
    if (!externalPaste) return
    void paste.handlePasteData(spreadsheet.activeCell?.row ?? 0, spreadsheet.activeCell?.col ?? 2, externalPaste.data)
    onExternalPasteHandled?.()
  }, [externalPaste?.nonce]) // eslint-disable-line react-hooks/exhaustive-deps

  // Navigation helpers
  const lastRowBySectionId = useMemo(() => {
    const m = new Map<string, number>()
    for (const item of flatLineItems) m.set(item.sectionId, item.rowIndex)
    return m
  }, [flatLineItems])

  const preferredSectionId = useCallback((): string | null => {
    if (focusedSectionId) return focusedSectionId
    if (activeSheet.type === 'section' && activeSheet.sectionId) return activeSheet.sectionId
    return sections[0]?.id || null
  }, [focusedSectionId, activeSheet.type, activeSheet.sectionId, sections])

  const focusPhantomRow = useCallback((sectionId: string, seedText?: string) => {
    const pi = rows.findIndex((r) => r.type === 'phantom' && r.section.id === sectionId)
    if (pi >= 0) virtualizer.scrollToIndex(pi, { align: 'end' })
    requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>(`input[data-phantom-desc="${sectionId}"]`)
      if (!input) return
      input.focus()
      if (seedText?.length === 1) {
        const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
        if (setter) { setter.call(input, (input.value || '') + seedText); input.dispatchEvent(new Event('input', { bubbles: true })) }
      }
    })
  }, [rows, virtualizer])

  useEffect(() => {
    if (!requestedPhantomFocusSectionId) return
    focusPhantomRow(requestedPhantomFocusSectionId)
    onRequestedPhantomFocusHandled?.()
  }, [requestedPhantomFocusSectionId, focusPhantomRow, onRequestedPhantomFocusHandled])

  useEffect(() => {
    if (!requestedScrollToSectionId) return
    const i = rows.findIndex((r) => r.type === 'section-header' && r.section.id === requestedScrollToSectionId)
    if (i >= 0) virtualizer.scrollToIndex(i, { align: 'start' })
    onRequestedScrollToSectionHandled?.()
  }, [requestedScrollToSectionId, rows, virtualizer, onRequestedScrollToSectionHandled])

  // Keyboard handler
  const handleGridKeyDown = useCallback((e: React.KeyboardEvent) => {
    const { key } = e
    const isCmd = e.ctrlKey || e.metaKey
    const isPrintable = key.length === 1 && !isCmd && !e.altKey
    const ac = spreadsheet.activeCell
    const sid = ac ? flatLineItems[ac.row]?.sectionId || null : preferredSectionId()

    // Excel-parity row ops on the active row.
    //   Ctrl/Cmd + Shift + =  →  Insert row above
    //   Ctrl/Cmd + =          →  Insert row below
    //   Ctrl/Cmd + -          →  Delete row
    //   Ctrl/Cmd + Backspace  →  Delete row (mac convention)
    // Active item is required; without a focused row we fall through.
    if (ac && isCmd && !spreadsheet.isEditing) {
      const flat = flatLineItems[ac.row]
      if (flat) {
        if ((key === '=' || key === '+') && e.shiftKey) {
          e.preventDefault()
          onInsertRowAbove?.(flat.sectionId, flat.itemId)
          return
        }
        if (key === '=' || key === '+') {
          e.preventDefault()
          onInsertRowBelow?.(flat.sectionId, flat.itemId)
          return
        }
        if (key === '-' || key === '_' || key === 'Backspace') {
          e.preventDefault()
          onDeleteItem(flat.sectionId, flat.itemId)
          return
        }
      }
    }

    if (!ac && sid) {
      if (key === 'Enter' || isPrintable) { e.preventDefault(); focusPhantomRow(sid, isPrintable ? key : undefined); return }
    }
    if (sid && ac) {
      const lastIdx = lastRowBySectionId.get(sid)
      if (key === 'Enter' && !e.shiftKey && typeof lastIdx === 'number' && lastIdx === ac.row) {
        if (!spreadsheet.isEditing) { e.preventDefault(); focusPhantomRow(sid); return }
        spreadsheet.handleKeyDown(e)
        requestAnimationFrame(() => focusPhantomRow(sid))
        return
      }
    }
    spreadsheet.handleKeyDown(e)
  }, [spreadsheet, flatLineItems, preferredSectionId, focusPhantomRow, lastRowBySectionId, onInsertRowAbove, onInsertRowBelow, onDeleteItem])

  return (
    <div className="flex flex-col h-full">
      {/* Focus Mode Indicator */}
      {focusedSection && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 text-sm">
            <Focus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-blue-700 dark:text-blue-300 font-medium">Focus Mode: {focusedSection.title}</span>
            <span className="text-blue-500 dark:text-blue-400 text-xs">({focusedSection.items?.length || 0} items)</span>
          </div>
          <button onClick={() => handleFocusSection(null)} className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded transition-colors">
            <X className="w-3.5 h-3.5" /> Exit Focus
          </button>
        </div>
      )}

      <ColumnHeaders
        columnBands={columnBands}
        allColumns={allColumns}
        gridStyle={gridStyle}
        visibleColumnIds={visibleColumnIds}
        onUpdateConfig={onUpdateConfig}
        onOpenSetup={activateAssumptions}
      />

      {/* Virtualized rows */}
      <div ref={parentRef} className="flex-1 overflow-auto focus:outline-none" tabIndex={0} onKeyDown={handleGridKeyDown}
        onFocus={() => { if (!isEditable || totalLineItems !== 0) return; const s = preferredSectionId(); if (s) focusPhantomRow(s) }}>
        <div style={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
          {virtualItems.map((vr) => {
            const row = rows[vr.index]
            return (
              <div key={vr.key} id={row.type === 'section-header' ? `section-${row.section.id}` : undefined}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: vr.size, transform: `translateY(${vr.start}px)` }}>
                {row.type === 'section-header' && (
                  <SectionHeaderRow section={row.section} isCollapsed={collapsedSections.has(row.section.id)}
                    onToggleCollapse={() => toggleCollapse(row.section.id)} onRename={(t) => onRenameSection(row.section.id, t)}
                    onDelete={() => onDeleteSection(row.section.id)} onFocus={() => toggleFocus(row.section.id)}
                    isFocused={focusedSectionId === row.section.id} isEditable={isEditable} />
                )}
                {row.type === 'line-item' && (
                  <LineItemRow section={row.section} item={row.item} index={row.index} flatRowIndex={row.flatRowIndex}
                    onUpdate={(patch) => onUpdateItem(row.section.id, row.item.id, patch)}
                    onDelete={() => onDeleteItem(row.section.id, row.item.id)}
                    onSelect={(colIndex, opts) => {
                      onSelectItem?.(row.section.id, row.item.id)
                      const tc = colIndex ?? 0
                      spreadsheet.navigateTo(row.flatRowIndex, tc)
                      if (opts?.startEditing) {
                        const cd = columnDefs[tc]
                        if (cd?.editable && !cd.hidden) { spreadsheet.setEditValue(getCellValue(row.flatRowIndex, tc)); spreadsheet.setIsEditing(true) }
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      onSelectItem?.(row.section.id, row.item.id)
                      setRowMenu({ x: e.clientX, y: e.clientY, sectionId: row.section.id, itemId: row.item.id })
                    }}
                    isSelected={selectedItemId === row.item.id || spreadsheet.activeCell?.row === row.flatRowIndex}
                    isEditable={isEditable}
                    activeCellCol={spreadsheet.activeCell?.row === row.flatRowIndex ? spreadsheet.activeCell.col : undefined}
                    isEditing={spreadsheet.activeCell?.row === row.flatRowIndex && spreadsheet.isEditing}
                    editValue={spreadsheet.editValue} onStartEditing={spreadsheet.startEditing}
                    onSetEditValue={spreadsheet.setEditValue}
                    onCommitEdit={(value, field) => onUpdateItem(row.section.id, row.item.id, { [field]: value })}
                    onCellKeyDown={handleGridKeyDown}
                    getOptimisticValue={getOptimisticValue} getCellErrorState={getCellErrorState} gridStyle={gridStyle}
                    customColumns={customColumns}
                    advancedColumns={advancedColumns} />
                )}
                {row.type === 'phantom' && (
                  <div className="h-full">
                    <PhantomRow sectionId={row.section.id} rowNumber={(row.section.items?.length ?? 0) + 1}
                      gridStyle={gridStyle} onAddItem={onAddItem} isEditable={isEditable}
                      configDefaults={configDefaults}
                      totalGridColumns={allColumns.length}
                      autoFocus={isEditable && totalLineItems === 0 && row.section.id === preferredSectionId()} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Paste Mapping Modal */}
      <PasteMappingModal isOpen={paste.pasteModalOpen} onClose={paste.closePasteModal}
        pasteData={paste.pendingPasteData || []} availableFields={paste.availablePasteFields}
        savedMapping={paste.savedMappingForModal} onApply={paste.handlePasteMappingApply}
        onSaveMapping={paste.handleSavePasteMapping}
        sectionName={paste.pendingPasteTarget ? sections.find((s) => s.id === paste.pendingPasteTarget!.sectionId)?.title || 'Section' : 'Section'} />

      {/* Right-click row context menu — Excel parity for row ops. */}
      {rowMenu && (
        <RowContextMenu
          x={rowMenu.x}
          y={rowMenu.y}
          readOnly={!isEditable}
          onClose={() => setRowMenu(null)}
          onAction={(action: RowContextAction) => {
            if (!rowMenu) return
            const { sectionId, itemId } = rowMenu
            switch (action) {
              case 'insert-above':
                onInsertRowAbove?.(sectionId, itemId)
                break
              case 'insert-below':
                onInsertRowBelow?.(sectionId, itemId)
                break
              case 'duplicate':
                onDuplicateItem?.(sectionId, itemId)
                break
              case 'clear-values':
                // Reset every editable scalar to null/empty. We avoid touching
                // ids/timestamps and leave per-line custom_values as-is so the
                // user keeps any opt-in cascade overrides they may have set.
                onUpdateItem(sectionId, itemId, {
                  description: '',
                  location: '',
                  application: '',
                  product_system: '',
                  quantity: 0,
                  productivity_pmpd: null,
                  labour_chargeout: null,
                  material_chargeout: null,
                  plant_chargeout: null,
                  subcon_chargeout: null,
                  adjusted_rate: null,
                  override_chargeout: null,
                  labour_cost: null,
                  material_cost: null,
                  plant_cost: null,
                  subcon_cost: null,
                  internal_notes: '',
                  client_visible_notes: '',
                  formulas: {},
                } as Partial<QuoteLineItem>)
                break
              case 'delete':
                onDeleteItem(sectionId, itemId)
                break
            }
          }}
        />
      )}
    </div>
  )
}
